import { JanusAPIv2Client } from '../client';
import {
    Options,
    AdminOptions,
    BaseRequestOptions,
    ExportOptions,
    ImportOptions,
    JanusApiError,
    User,
    SecretStub,
    Secret,
    Zone,
    DashboardData,
    LogEntry,
    LogLevelInfo,
    UpdateUserDto,
    LogQueryOptions,
    DataItemMetadata,
    ZoneConfigurationDto,
    ImportValidationResult,
    ImportDryRunResult,
    ImportExecutionResult,
} from '../types';

// Define UpdateMetadataDto locally if not exported from models
interface UpdateAdminStorageMetadataDto {
    metadata: Record<string, any>;
}

export class AdminClient {
    private client: JanusAPIv2Client;

    constructor(client: JanusAPIv2Client) {
        this.client = client;
    }

    // Helper to encode path components safely, especially for storage keys
    private encodeStorageKey(key: string): string {
        const parts = key.split('/');
        if (parts.length === 0) return ''; // Handle empty key
        // Ensure the first part (zone) is a string before encoding
        const zone = encodeURIComponent(String(parts[0])); 
        if (parts.length === 1) return zone; // Handle zone-only key
        const path = parts.slice(1).map(p => encodeURIComponent(String(p))).join('/'); 
        return `${zone}/${path}`;
    }

    // === Dashboard ===
    async getDashboardData(options?: AdminOptions): Promise<DashboardData> {
        return this.client._request<DashboardData>('GET', 'admin/dashboard', options);
    }

    // === User Management ===
    async listUsers(options?: AdminOptions): Promise<User[]> {
        return this.client._request<User[]>('GET', 'admin/users', options);
    }

    async getUser(userId: string, options?: AdminOptions): Promise<User> {
        if (!userId) throw new Error('User ID cannot be empty');
        const path = `admin/users/${encodeURIComponent(userId)}`;
        return this.client._request<User>('GET', path, options);
    }

    /**
     * Update user details (displayName, status, roles).
     * Note: The backend PUT endpoint might handle partial updates.
     */
    async updateUser(userId: string, updates: UpdateUserDto, options?: AdminOptions): Promise<User> {
        if (!userId) throw new Error('User ID cannot be empty');
        if (!updates || Object.keys(updates).length === 0) {
            throw new Error('Updates cannot be empty');
        }
        const path = `admin/users/${encodeURIComponent(userId)}`;
        return this.client._request<User>('PUT', path, { ...options, body: updates });
    }

    /**
     * Delete a user (marks as inactive/banned).
     */
    async deleteUser(userId: string, options?: AdminOptions): Promise<User> {
        if (!userId) throw new Error('User ID cannot be empty');
        const path = `admin/users/${encodeURIComponent(userId)}`;
        return this.client._request<User>('DELETE', path, options);
    }

    // === Storage Management (Admin View) ===
    async listStorageItems(options?: AdminOptions): Promise<DataItemMetadata[]> {
        return this.client._request<DataItemMetadata[]>('GET', 'admin/storage', options);
    }

    async getStorageItemMetadata(key: string, options?: AdminOptions): Promise<DataItemMetadata> {
        if (!key) throw new Error('Storage key cannot be empty');
        const path = `admin/storage/${encodeURIComponent(key)}`;
        return this.client._request<DataItemMetadata>('GET', path, options);
    }

    async deleteStorageItem(key: string, options?: AdminOptions): Promise<{ success: boolean }> {
        if (!key) throw new Error('Storage key cannot be empty');
        const path = `admin/storage/${encodeURIComponent(key)}`;
        return this.client._request<{ success: boolean }>('DELETE', path, options);
    }

    async updateStorageItemMetadata(key: string, metadata: Record<string, any>, options?: AdminOptions): Promise<DataItemMetadata> {
        if (!key) throw new Error('Storage key cannot be empty');
        if (!metadata || Object.keys(metadata).length === 0) {
            throw new Error('Metadata cannot be empty');
        }
        const path = `admin/storage/${encodeURIComponent(key)}`;
        return this.client._request<DataItemMetadata>('PATCH', path, { ...options, body: { metadata } });
    }

    // === Secrets Management ===
    async listSecrets(options?: AdminOptions): Promise<SecretStub[]> {
        return this.client._request<SecretStub[]>('GET', 'admin/secrets', options);
    }

    async getSecret(secretName: string, options?: AdminOptions): Promise<Secret> {
        if (!secretName) throw new Error('Secret name cannot be empty');
        const path = `admin/secrets/${encodeURIComponent(secretName)}`;
        return this.client._request<Secret>('GET', path, options);
    }
    
    // --- Enhanced secrets management with description support --- 
    async upsertSecret(secretName: string, secretData: { value: string; description?: string }, options?: AdminOptions): Promise<Secret> {
        if (!secretName) throw new Error('Secret name cannot be empty');
        if (!secretData || !secretData.value) {
            throw new Error('Secret value cannot be empty');
        }
        const path = `admin/secrets/${encodeURIComponent(secretName)}`;
        return this.client._request<Secret>('PUT', path, { ...options, body: secretData });
    }
    // --- 

    async deleteSecret(secretName: string, options?: AdminOptions): Promise<{ success: boolean, message?: string }> {
        if (!secretName) throw new Error('Secret name cannot be empty');
        const path = `admin/secrets/${encodeURIComponent(secretName)}`;
        return this.client._request<{ success: boolean, message?: string }>('DELETE', path, options);
    }

    // === Zone Management ===
    async listZones(): Promise<ZoneConfigurationDto[]> {
        return this.client._request<ZoneConfigurationDto[]>('GET', '/admin/zones');
    }

    // === Content Management ===
    async reloadContent(options?: AdminOptions): Promise<{ 
        success: boolean; 
        message: string; 
        details?: {
            duration: number;
            publicContentBefore: number;
            publicContentAfter: number;
            publicContentChange: number;
            archivesProcessed: number;
        }
    }> {
        return this.client._request<{ 
            success: boolean; 
            message: string; 
            details?: {
                duration: number;
                publicContentBefore: number;
                publicContentAfter: number;
                publicContentChange: number;
                archivesProcessed: number;
            }
        }>(
            'POST',
            'admin/content/reload',
            options,
        );
    }

    /**
     * Delete all content in a specified zone
     * @param zone The zone to clear completely
     * @param options Request options
     * @returns Promise resolving to deletion result
     */
    async deleteAllZoneContent(zone: string, options?: AdminOptions): Promise<{ success: boolean; deletedCount: number; message: string }> {
        if (!zone) throw new Error('Zone cannot be empty');
        return this.client._request<{ success: boolean; deletedCount: number; message: string }>(
            'DELETE',
            `admin/content/zones/${encodeURIComponent(zone)}`,
            options
        );
    }

    /**
     * Delete content in a zone matching a path prefix
     * @param zone The zone to delete from
     * @param pathPrefix The path prefix to match (minimum 1 character)
     * @param options Request options
     * @returns Promise resolving to deletion result
     */
    async deleteZoneContentByPath(zone: string, pathPrefix: string, options?: AdminOptions): Promise<{ success: boolean; deletedCount: number; message: string }> {
        if (!zone) throw new Error('Zone cannot be empty');
        if (!pathPrefix || pathPrefix.trim().length < 1) {
            throw new Error('Path prefix must be at least 1 character long');
        }
        const encodedZone = encodeURIComponent(zone);
        const encodedPathPrefix = encodeURIComponent(pathPrefix.trim());
        return this.client._request<{ success: boolean; deletedCount: number; message: string }>(
            'DELETE',
            `admin/content/zones/${encodedZone}/path/${encodedPathPrefix}`,
            options
        );
    }

    // === Export/Import Management ===
    
    /**
     * Export data items as a compressed archive (default tar.zst format)
     * @param paths Array of path specifiers to include in the export
     * @returns Promise resolving to a Blob containing the compressed archive data
     * 
     * @example
     * // Export as tar.zst (default)
     * const archive = await client.admin.export(['path1', 'path2']);
     */
    async export(paths: string[]): Promise<Blob>;
    
    /**
     * Export data items as a compressed archive with specified format
     * @param paths Array of path specifiers to include in the export  
     * @param format Export format - 'tar.zst' or 'zip'
     * @returns Promise resolving to a Blob containing the compressed archive data
     * 
     * @example
     * // Export as ZIP
     * const zipArchive = await client.admin.export(['path1', 'path2'], 'zip');
     */
    async export(paths: string[], format: 'tar.zst' | 'zip'): Promise<Blob>;
    
    /**
     * Export data items as a compressed archive with custom options
     * @param paths Array of path specifiers to include in the export
     * @param options Export options including format specification
     * @returns Promise resolving to a Blob containing the compressed archive data
     * 
     * @example
     * // Export with custom options
     * const archive = await client.admin.export(['path1', 'path2'], {
     *   body: { format: 'zip' },
     *   headers: { 'Accept': 'application/zip' }
     * });
     */
    async export(paths: string[], options: ExportOptions): Promise<Blob>;
    
    /**
     * Implementation of the export method with runtime type safety
     */
    async export(
        paths: string[], 
        formatOrOptions?: 'tar.zst' | 'zip' | ExportOptions
    ): Promise<Blob> {
        // Runtime parameter validation
        if (!paths || paths.length === 0) {
            throw new Error('At least one path must be specified for export');
        }
        
        // Runtime type checking to prevent the original bug
        if (typeof formatOrOptions === 'string') {
            // Handle format parameter case - this is the intended usage
            const format = formatOrOptions;
            const body = { paths, format };
            const response = await this.client._request('POST', 'admin/export', {
                body,
                responseType: 'blob'
            });
            return response as Blob;
        } else if (formatOrOptions && typeof formatOrOptions === 'object') {
            // Handle ExportOptions case
            const options = formatOrOptions;
            // Extract format from options.body if present, otherwise default to tar.zst
            const format = options.body?.format || 'tar.zst';
            const body = { paths, format };
            
            // Validate the options parameter to catch potential misuse
            const validOptionKeys = ['headers', 'params', 'responseType', 'skipContentTypeHeader', 'body'];
            const providedKeys = Object.keys(options);
            const invalidKeys = providedKeys.filter(key => !validOptionKeys.includes(key));
            if (invalidKeys.length > 0) {
                throw new Error(`Invalid export options provided: ${invalidKeys.join(', ')}. Valid options are: ${validOptionKeys.join(', ')}`);
            }
            
            const response = await this.client._request('POST', 'admin/export', {
                ...options,
                body,
                responseType: 'blob'
            });
            return response as Blob;
        } else {
            // Default case - no format specified, use tar.zst
            const body = { paths, format: 'tar.zst' };
            const response = await this.client._request('POST', 'admin/export', {
                body,
                responseType: 'blob'
            });
            return response as Blob;
        }
    }

    /**
     * Validate the integrity of an import archive (auto-detects .tar.zst or .zip format)
     * @param archiveFile A File or Blob object of the archive (.tar.zst or .zip)
     * @param options Request options
     * @returns Promise resolving to validation result with item count
     */
    async importValidate(archiveFile: File | Blob, options?: ImportOptions): Promise<ImportValidationResult> {
        if (!archiveFile) {
            throw new Error('Archive file is required for validation');
        }
        
        // Extract basePath and pathFilter from options to pass as query parameters
        const { basePath, pathFilter, ...requestOptions } = options || {};
        const params: Record<string, string | undefined> = {};
        if (basePath) params.basePath = basePath;
        if (pathFilter) params.pathFilter = pathFilter;
        
        return this.client._request('POST', 'admin/import/validate', {
            body: archiveFile,
            headers: { 'Content-Type': 'application/octet-stream' },
            params,
            ...requestOptions
        });
    }

    /**
     * Perform a dry-run analysis of an import archive (auto-detects .tar.zst or .zip format)
     * @param archiveFile A File or Blob object of the archive (.tar.zst or .zip)
     * @param options Request options  
     * @returns Promise resolving to detailed report of potential changes
     */
    async importDryRun(archiveFile: File | Blob, options?: ImportOptions): Promise<ImportDryRunResult> {
        if (!archiveFile) {
            throw new Error('Archive file is required for dry-run analysis');
        }
        
        // Extract basePath and pathFilter from options to pass as query parameters
        const { basePath, pathFilter, ...requestOptions } = options || {};
        const params: Record<string, string | undefined> = {};
        if (basePath) params.basePath = basePath;
        if (pathFilter) params.pathFilter = pathFilter;
        
        return this.client._request('POST', 'admin/import/dry-run', {
            body: archiveFile,
            headers: { 'Content-Type': 'application/octet-stream' },
            params,
            ...requestOptions
        });
    }

    /**
     * Execute an import from an archive (auto-detects .tar.zst or .zip format)
     * @param archiveFile A File or Blob object of the archive (.tar.zst or .zip)
     * @param options Request options
     * @returns Promise resolving to report of completed import operations
     */
    async importExecute(archiveFile: File | Blob, options?: ImportOptions): Promise<ImportExecutionResult> {
        if (!archiveFile) {
            throw new Error('Archive file is required for import execution');
        }
        
        // Extract basePath and pathFilter from options to pass as query parameters
        const { basePath, pathFilter, ...requestOptions } = options || {};
        const params: Record<string, string | undefined> = {};
        if (basePath) params.basePath = basePath;
        if (pathFilter) params.pathFilter = pathFilter;
        
        return this.client._request('POST', 'admin/import/execute', {
            body: archiveFile,
            headers: { 'Content-Type': 'application/octet-stream' },
            params,
            ...requestOptions
        });
    }

    // === Log Management (Moved to LogsController/LogsClient, but kept for reference if needed) ===
    // async getLogLevels(options?: Options): Promise<LogLevelInfo[]> {
    //     return this.client._request('GET', 'admin/logs/levels', options);
    // }

    // async getLogs(query?: LogQueryOptions, options?: Options): Promise<LogEntry[]> {
    //     return this.client._request('GET', 'admin/logs', { params: query, ...options });
    // }

    // async clearLogs(options?: Options): Promise<{ success: boolean }> {
    //     return this.client._request('POST', 'admin/logs/clear', options);
    // }
} 